import { EmptyStateProps, Table } from '@dixa/lib-shared-habla';
import { Satisfaction } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import { ratingApi } from 'apis/rating';

import { SurveyLanguageTableRow } from './row/surveyLanguageTableRow';

type SurveyLanguagesTableProps = {
  handleGoToLanguage: (lang: string) => void;
  surveyCsatSettings: Satisfaction.CsatSettings;
};

export function SurveyLanguagesTable(
  props: SurveyLanguagesTableProps,
): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);

  const [
    readRatingSettings,
    {
      data: readRatingSettingsResponse,
      isFetching: readRatingSettingsIsFetching,
      isSuccess: readRatingSettingsIsSuccess,
    },
  ] = ratingApi.useLazyReadRatingSettingsQuery();

  const listLanguageSettings = useCallback(() => {
    readRatingSettings({
      parentId: props.surveyCsatSettings.id,
      level: 'language',
      /**
       * Once we support multiple surveys we will this will have to come from
       * the survey rating settings object
       */
      surveyType: props.surveyCsatSettings.survey_type,
    });
  }, [props.surveyCsatSettings, readRatingSettings]);

  useMount(() => {
    listLanguageSettings();
  });

  useEffect(() => {
    setSurveyLanguagesSettings(
      readRatingSettingsResponse?.data.rating_settings ?? [],
    );
    setIsLoading(readRatingSettingsIsFetching);
  }, [readRatingSettingsResponse, readRatingSettingsIsFetching]);

  const [surveyLanguages, setSurveyLanguagesSettings] = useState<
    Satisfaction.CsatSettings[]
  >([]);

  const emptyState: EmptyStateProps | undefined = useMemo(() => {
    return readRatingSettingsIsSuccess && surveyLanguages.length < 1
      ? {
          title: 'No configured languages',
          description:
            'Click "Add language" to set up a language for your CSAT survey.',
        }
      : undefined;
  }, [readRatingSettingsIsSuccess, surveyLanguages]);

  return (
    <Table
      framed
      stickyHeader
      headerWithBackground
      loadingFailed={!readRatingSettingsIsSuccess}
      loadingStrategy="spinner"
      loading={isLoading}
      emptyState={emptyState}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th columnId="language">Language</Table.Th>

          <Table.Th columnId="priority" width="0">
            Enabled
          </Table.Th>

          <Table.Th columnId={null} width="0" />
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {surveyLanguages.map((settings) => (
          <SurveyLanguageTableRow
            key={settings.id}
            languageSettings={settings}
            handleGoToLanguage={props.handleGoToLanguage}
            onDelete={listLanguageSettings}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}
